import { useState, useEffect, useRef, useMemo, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useRole } from '../hooks/useRole';
import { useTranslation, languages, Language } from '../i18n';
import { useNbTheme } from '../hooks/useNbTheme';
import { useClickOutside } from '../hooks/useClickOutside';
import ButtonNB from './ui-nb/ButtonNB';
import IconNB from './ui-nb/IconNB';
import SelectNB from './ui-nb/SelectNB';
import ThemeToggleNB from './ThemeToggleNB';
import BadgeNB from './ui-nb/BadgeNB';
import { error as logError } from '../lib/logger';
import { getErrorMessage } from '../lib/errors';

interface TopNavLayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
  adminOnly?: boolean;
  badge?: number;
}

export default function TopNavLayout({ children }: TopNavLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const user = useAuthStore(s => s.user);
  const signOut = useAuthStore(s => s.signOut);
  const { t, lang, setLanguage } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const { colors } = useNbTheme();
  const menuRef = useRef<HTMLElement>(null);
  const userRole = useRole();

  useClickOutside(menuRef, () => setMobileMenuOpen(false), mobileMenuOpen);

  // Auth guard — redirect to landing if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/${lang || 'en'}`, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Poll for alerts count every 60 seconds
  useEffect(() => {
    if (!user?.organizationId) return;

    let cancelled = false;

    async function fetchAlertCount() {
      try {
        const { supabase } = await import('../lib/supabase');

        // Count delayed orders (past estimated_date, not completed)
        if (!user) return;
        const { count: delayedCount } = await supabase
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .eq('branches.organization_id', user.organizationId)
          .is('completed_at', null)
          .lt('estimated_date', new Date().toISOString());

        if (!cancelled && typeof delayedCount === 'number') {
          setAlertCount(delayedCount);
        }
      } catch (err) {
        logError('Error fetching alert count', { error: getErrorMessage(err) });
      }
    }

    // Initial fetch
    fetchAlertCount();

    // Set up 60s polling interval
    const intervalId = setInterval(fetchAlertCount, 60000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user?.organizationId]);

  const navItems = useMemo<NavItem[]>(() => {
    const locale = lang || 'en';
    const isAdmin = userRole === 'admin';
    const allItems: NavItem[] = [
      { path: `/${locale}/${isAdmin ? 'admin' : 'technician'}`, label: t('nav.dashboard') || 'Dashboard', icon: 'dashboard' },
      { path: `/${locale}/admin/orders`, label: t('nav.orders') || 'Orders', icon: 'orders', adminOnly: true },
      { path: `/${locale}/admin/spare-parts`, label: t('nav.spareParts') || 'Refacciones', icon: 'package' },
      { path: `/${locale}/admin/branches`, label: t('nav.branches') || 'Branches', icon: 'branches', adminOnly: true },
      { path: `/${locale}/admin/users`, label: t('nav.users') || 'Users', icon: 'users', adminOnly: true },
      { path: `/${locale}/admin/reports`, label: t('nav.reports') || 'Reports', icon: 'reports', adminOnly: true },
      { path: `/${locale}/admin/alerts`, label: t('nav.alerts') || 'Alerts', icon: 'bell', adminOnly: true, badge: alertCount },
      { path: `/${locale}/admin/config`, label: t('nav.settings') || 'Settings', icon: 'settings', adminOnly: true },
    ];
    if (isAdmin) return allItems;
    return allItems.filter(item => !item.adminOnly);
  }, [lang, t, userRole, alertCount]);

  const isActive = (path: string) => {
    // Exact match for root paths to avoid matching sub-paths
    if (path.endsWith('/admin') || path.endsWith('/technician')) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLanguageChange = (newLang: Language) => {
    setLanguage(newLang);
    const target = userRole === 'admin' ? `/${newLang}/admin` : `/${newLang}/technician`;
    navigate(target);
  };

  const handleSignOut = () => {
    signOut();
    navigate(`/${lang || 'en'}`);
  };

  const userInitial = user?.name?.charAt(0).toUpperCase() || 'U';

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: colors.bgPage }}>
      {/* Top Navigation Bar */}
      <header ref={menuRef} className="border-b-3 sticky top-0 z-50 relative" style={{ background: colors.bgCard, borderColor: colors.textPrimary }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo / Brand */}
            <div className="flex items-center">
              <span className="font-nb font-bold text-xl" style={{ color: colors.textPrimary }}>TellowUp</span>
            </div>

            {/* Desktop Navigation — only on xl+ screens where all tabs fit */}
            <nav className="hidden xl:flex items-center gap-2">
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    className="flex items-center gap-2 px-4 py-2 font-nb font-bold text-sm border-3 rounded-[2px] transition-transform duration-200 hover:translate-x-1 hover:border-4"
                    style={{
                      background: active ? colors.textPrimary : colors.bgCard,
                      color: active ? colors.bgCard : colors.textPrimary,
                      borderColor: colors.textPrimary,
                    }}
                  >
                    <IconNB name={item.icon} size={20} />
                    {item.label}
                    {item.badge !== undefined && item.badge > 0 && (
                      <BadgeNB variant="error" size="sm">
                        {item.badge > 99 ? '99+' : item.badge}
                      </BadgeNB>
                    )}
                  </a>
                );
              })}
            </nav>

            {/* User Section */}
            <div className="hidden xl:flex items-center gap-4">
              <ThemeToggleNB />
              <SelectNB
                value={lang}
                onChange={(e) => handleLanguageChange(e.target.value as Language)}
                selectSize="sm"
              >
                {languages.map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </SelectNB>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-nb font-bold text-lg" style={{ background: colors.textPrimary, color: colors.bgCard }}>
                  {userInitial}
                </div>
                <span className="font-nb text-sm" style={{ color: colors.textPrimary }}>{user?.name}</span>
              </div>
              <ButtonNB variant="danger" size="sm" onClick={handleSignOut}>
                Logout
              </ButtonNB>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="xl:hidden p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              style={{ color: colors.textPrimary }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Panel — floats over content */}
        {mobileMenuOpen && (
          <div
            className="xl:hidden absolute top-full left-0 right-0 border-t-3 shadow-lg"
            style={{ borderColor: colors.textPrimary, background: colors.bgCard }}
          >
            <nav className="flex flex-col gap-2 p-4 max-h-[70vh] overflow-y-auto">
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    className="flex items-center gap-2 px-4 py-3 font-nb font-bold text-sm border-3 rounded-[2px] transition-all duration-150 hover:border-4"
                    style={{
                      background: active ? colors.textPrimary : colors.bgCard,
                      color: active ? colors.bgCard : colors.textPrimary,
                      borderColor: colors.textPrimary,
                    }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <IconNB name={item.icon} size={20} />
                    {item.label}
                    {item.badge !== undefined && item.badge > 0 && (
                      <BadgeNB variant="error" size="sm">
                        {item.badge > 99 ? '99+' : item.badge}
                      </BadgeNB>
                    )}
                  </a>
                );
              })}

              {/* Mobile User Section */}
              <div className="flex items-center gap-3 mt-4 pt-4 border-t-3" style={{ borderColor: colors.textPrimary }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-nb font-bold text-lg" style={{ background: colors.textPrimary, color: colors.bgCard }}>
                  {userInitial}
                </div>
                <span className="font-nb text-sm" style={{ color: colors.textPrimary }}>{user?.name}</span>
              </div>
              <ButtonNB variant="danger" size="md" onClick={handleSignOut} className="mt-2 w-full">
                Logout
              </ButtonNB>
              <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.textPrimary }}>
                <SelectNB
                  value={lang}
                  onChange={(e) => handleLanguageChange(e.target.value as Language)}
                  selectSize="sm"
                >
                  {languages.map((l) => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  ))}
                </SelectNB>
              </div>
              <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.textPrimary }}>
                <ThemeToggleNB />
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content Area */}
      <main className="flex-1 p-4 md:p-8" style={{ backgroundColor: colors.bgPage }}>
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
